import { create } from "zustand";

import { useBundleStore } from "@/stores/bundleStore";
import { useLayoutStore } from "@/stores/layoutStore";

export type SourceKind = "pptx" | "pdf";

/**
 * The single source-of-truth for "which file is currently loaded".
 *
 * Only ONE file (PDF or PPTX) is held at a time. Uploading a *different* file
 * overwrites the previous one and clears both derived results (the /app JSON
 * bundle and the /layout analysis), so stale data from the old file never
 * lingers. Re-deriving a view for the SAME file (e.g. opening the layout view
 * for a PPTX already converted on /app) does NOT clear the other view.
 *
 * Held in memory only — a File object can't be persisted.
 */
type State = {
  file: File | null;
  kind: SourceKind | null;
  setFile: (f: File | null) => void;
  clear: () => void;
};

export function kindOf(file: File | null): SourceKind | null {
  if (!file) return null;
  const name = file.name.toLowerCase();
  if (name.endsWith(".pdf")) return "pdf";
  if (name.endsWith(".pptx")) return "pptx";
  return null;
}

function sameFile(a: File | null, b: File | null): boolean {
  return !!a && !!b && a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

export const useSourceStore = create<State>((set, get) => ({
  file: null,
  kind: null,
  setFile: (f) => {
    const changed = !sameFile(get().file, f);
    set({ file: f, kind: kindOf(f) });
    if (changed) {
      // New source overwrites prior data — only one of PDF/PPTX at a time.
      useBundleStore.getState().reset();
      useLayoutStore.getState().reset();
    }
  },
  clear: () => {
    set({ file: null, kind: null });
    useBundleStore.getState().reset();
    useLayoutStore.getState().reset();
  },
}));
